import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import type { OpcionAnotada } from '@/engine/narrative';
import { parseMarkup } from '@/engine/narrative';
import { MarkupSpan } from './MarkupText';
import { useTextoResuelto } from '@/hooks/useTextoResuelto';
import { cn } from '@/utils/cn';

interface ChoiceListProps {
  opciones: OpcionAnotada[];
  onElegir: (choiceId: string) => void;
  visible: boolean;
}

const INTENCION_COLOR: Record<string, string> = {
  empatico: 'border-moss-600/50 hover:border-moss-500',
  valiente: 'border-ember-600/50 hover:border-ember-500',
  astuto: 'border-mystic-500/50 hover:border-mystic-400',
  neutral: 'border-ash-700 hover:border-ash-500',
  hostil: 'border-red-800/60 hover:border-red-700',
};

export function ChoiceList({ opciones, onElegir, visible }: ChoiceListProps) {
  const visibles = opciones.filter((o) => !o.oculta);

  if (!visible || visibles.length === 0) return null;

  return (
    <motion.ul
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="grid gap-2 w-full"
    >
      {visibles.map((opcion, i) => (
        <ChoiceButton
          key={opcion.choice.id}
          opcion={opcion}
          indice={i}
          onElegir={onElegir}
        />
      ))}
    </motion.ul>
  );
}

function ChoiceButton({
  opcion,
  indice,
  onElegir,
}: {
  opcion: OpcionAnotada;
  indice: number;
  onElegir: (choiceId: string) => void;
}) {
  const { choice, habilitada, motivoNoDisponible } = opcion;
  const intencion = choice.intencion ?? 'neutral';
  // Resolver markup condicional (`[if item=...]`) antes de parsear.
  const textoResuelto = useTextoResuelto(choice.texto);
  const nodes = parseMarkup(textoResuelto);

  return (
    <motion.li
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.08 + indice * 0.05, duration: 0.3 }}
    >
      <button
        type="button"
        disabled={!habilitada}
        onClick={() => habilitada && onElegir(choice.id)}
        className={cn(
          'w-full text-left px-4 py-2.5 rounded-md border bg-ash-950/80 backdrop-blur-sm',
          'transition-all duration-200',
          'text-narrative text-ash-100 fluid-base leading-tight',
          'disabled:cursor-not-allowed disabled:opacity-45',
          INTENCION_COLOR[intencion] ?? INTENCION_COLOR.neutral,
          !habilitada && 'italic',
        )}
      >
        <div className="flex items-start gap-2">
          {!habilitada && (
            <Lock className="w-3.5 h-3.5 mt-1 shrink-0 text-ash-500" />
          )}
          <div className="flex-1 min-w-0">
            <span className="block clamp-3">
              {nodes.map((node, idx) => (
                <MarkupSpan key={idx} node={node} maxChars={node.value.length} />
              ))}
            </span>
            {!habilitada && motivoNoDisponible && (
              <span className="block fluid-xs font-ui text-ash-500 mt-0.5 uppercase tracking-wider not-italic clamp-2">
                {motivoNoDisponible}
              </span>
            )}
          </div>
        </div>
      </button>
    </motion.li>
  );
}
